import { FC, useEffect } from "react";

import { FormType, getDonationOrTicketingFormLink, DonationFormCategory } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { AmplitudeEvents } from "../../../../../common/constants/amplitude";
import { useCurrentUserContext } from "../../../../../common/contexts/CurrentUserContext";
import { useLocaleContext } from "../../../../../common/contexts/LocaleContext";
import { useIsOnboarding } from "../../../../../common/hooks/useIsOnboarding";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { LiveFormActionFooterPortal } from "../../common/LiveFormEditorActionFooter";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";

import { StepReviewStateless } from "./StepReview.stateless";
import { useActivateDonationForm } from "./useActivateDonationForm";

import { FROM_ONBOARDING_QUERY_PARAM } from "@/common/constants/url";
import { useUpdateDonationDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";

export const DonationLiveEditorStepReview: FC = () => {
  const { t } = useTranslate();
  const {
    setEditOrCreateMode,
    goToPreviousStep,
    apiFormObject: apiDonationForm,
    shareFormModalState,
    setIsNavigationOpen,
  } = useLiveFormEditorContext<"donation-form">();
  const { isoLocale } = useLocaleContext();
  const { firstPaymentDate } = useCurrentUserContext();

  const form = useForm();

  const { isOnboarding } = useIsOnboarding();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  // Always set to edit mode on this step
  useEffect(() => {
    setIsNavigationOpen(false);
    setEditOrCreateMode("edit");
  }, [setEditOrCreateMode, setIsNavigationOpen]);

  const formLink = getDonationOrTicketingFormLink({
    form: {
      id: apiDonationForm.id,
      path: apiDonationForm.path,
      formCategory: apiDonationForm.category,
      formType: FormType.DonationForm,
      isPrimaryCampaign: apiDonationForm.isPrimaryCampaign,
      isPrimaryTeam: apiDonationForm.isPrimaryTeam,
    },
    isoLocale,
  });

  const formLinkWithContext = isOnboarding
    ? `${formLink}${formLink.includes("?") ? "&" : "?"}${FROM_ONBOARDING_QUERY_PARAM}=true`
    : formLink;

  useActivateDonationForm();

  useUpdateDonationDataInPreview({
    form,
    mutate: () => {},
  });

  const stepTitle =
    apiDonationForm.category === DonationFormCategory.PeerToPeerCampaign
      ? t("dashboard", "donationForm.editor.stepReview.p2p.title")
      : t("dashboard", "donationForm.editor.stepReview.title");

  return (
    <>
      <LiveFormActionFooterPortal
        onBack={() => {
          logUserActionEvent(AmplitudeEvents.FormBackButtonClicked);
          goToPreviousStep();
        }}
      />
      <StepReviewStateless
        title={stepTitle}
        description={t("dashboard", `editor.stepReview.description`)}
        isOnboarding={isOnboarding}
        formLink={formLinkWithContext}
        showCommunicationCard={Boolean(firstPaymentDate)}
        formId={apiDonationForm.id}
        formStatus={apiDonationForm.status}
        onShareForm={shareFormModalState.openModal}
        onLogUserAction={logUserActionEvent}
        onNavigate={(path) => Promise.resolve(window.open(path, "_blank"))}
        communicationTexts={{
          title: t("dashboard", "setupDonationFormCommunications.title"),
          description: t("dashboard", "setupDonationFormCommunications.description"),
        }}
      />
    </>
  );
};
